interface LoadingStateProps {
  variant?: 'spinner' | 'skeleton' | 'card-skeleton';
  count?: number;
  message?: string;
  className?: string;
}

function Spinner({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-12" role="status" aria-live="polite">
      <svg
        className="h-8 w-8 animate-spin text-primary-600"
        fill="none"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      {message && <p className="mt-3 text-sm text-gray-500">{message}</p>}
      <span className="sr-only">Loading...</span>
    </div>
  );
}

function SkeletonLine({ width = 'w-full' }: { width?: string }) {
  return (
    <div className={`h-4 animate-pulse rounded bg-gray-200 ${width}`} aria-hidden="true" />
  );
}

function CardSkeleton() {
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm" aria-hidden="true">
      <div className="aspect-[4/3] w-full animate-pulse bg-gray-200" />
      <div className="space-y-2 p-3">
        <SkeletonLine width="w-3/4" />
        <SkeletonLine width="w-1/2" />
        <SkeletonLine width="w-2/3" />
      </div>
    </div>
  );
}

export function LoadingState({
  variant = 'spinner',
  count = 4,
  message,
  className = '',
}: LoadingStateProps) {
  if (variant === 'spinner') {
    return (
      <div className={className}>
        <Spinner message={message} />
      </div>
    );
  }

  if (variant === 'skeleton') {
    return (
      <div className={`space-y-3 ${className}`} role="status" aria-live="polite">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="space-y-2 rounded-lg bg-white p-4 shadow-sm">
            <SkeletonLine width="w-3/4" />
            <SkeletonLine width="w-1/2" />
          </div>
        ))}
        <span className="sr-only">Loading...</span>
      </div>
    );
  }

  // card-skeleton
  return (
    <div
      className={`grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 ${className}`}
      role="status"
      aria-live="polite"
    >
      {Array.from({ length: count }, (_, i) => (
        <CardSkeleton key={i} />
      ))}
      <span className="sr-only">Loading...</span>
    </div>
  );
}
